/* eslint no-console:0 */
import axios from 'axios';
import { isExternalMethodImplemented, acpGet, acpPost, acpSubscribe, fetchSmartScanResults } from './externalMethods';
import { getTokenFromCookies } from './utils';
import { logLevels } from '../constants/appConstants';
import logger from './logger';

const config = _CONFIG_;

/**
 * @description List of endpoints
 */
const endpoint = {
    appIntegrations: '/v2/app-integrations?filter[from]={bundleId}&filter[to]={filterTo}&include=from',
    appPrices: '/v2/app-prices/?filter[apps]={appId}',
    dashboardUrls: '/v2/dashboard-urls/?filter[section]={redirect_uri}',
    me: '/v2/me',
    myDevice: '/v2/my-device',
    paymentUrls: '/v2/payment-urls?filter[app]={appId}&filter[source]={source}&filter[language]={lang}&filter[runtime]={runtime}&filter[no_devices]={devices}',
    oauth: '/v2/oauth',
    promotions: '/v2/flexible-promotions/?filter[type]={type}&filter[source]={source}&filter[purchase_type]={purchaseType}&filter[product]={product}&filter[runtime]={runtime}&filter[devices]={devices}',
    licenses: '/v2/licenses/{id}',
    appRecommendations: '/v2/app-recommendations',
    payments: '/v2/payments',
    transactions: '/v2/transactions?filter[operation]=payment',
    paymentTypes: '/v2/payment-types',
    ping: '/v2/ping',
    appInstances: '/v2/app-instances?filter[device]={deviceId}',
    apps: '/v2/apps/{bundleId}',
    passwManLicense: '/v2/licenses?filter[app.service]=passm&filter[best_expiration]=1',
};

/**
 * @description Make axios calls with retry
 * @param {string} url
 * @param {function} callback
 */
const apiGet = (url, callback) => {
    const MAX_RETRIES = config.retry.max_retries;
    const RETRY_INTERVAL = config.retry.timeout;
    let RETRY_ATTEMPT = 0;

    let token = sessionStorage.getItem('SPOTLIGHT_TOKEN');
    // Get cwDashboard token from cookie if access_token param is missing
    if (!token) {
        token = getTokenFromCookies();
    }

    const options = {
        headers: {
            Authorization: `Bearer ${token}`,
        },
        timeout: config.timeouts.httpTimeout,
    };
    const endPointUrl = `${config.restApi.url}${url}`;
    const makeApiCall = (apiUrl, callbackMethod) =>
        axios.get(endPointUrl, options)
            .then(response => callback(response.data))
            .catch(error => {
                // check if the error code is >5xx
                const errorCode = String(error.response.status);
                const isServerError =
                        errorCode.lastIndexOf('5', 0) === 0 && errorCode.length === 3;
                const shouldRetryRequest =
                        isServerError || errorCode === '-1' || errorCode === '0' || errorCode === '404';

                if (shouldRetryRequest && RETRY_ATTEMPT < MAX_RETRIES) {
                    RETRY_ATTEMPT += 1;

                    if (errorCode === 404) {
                        sessionStorage.setItem('SPOTLIGHT_RETRY_CALL', true);
                    }

                    logger(logLevels.DEBUG, `Retry call on enpoint:${url}, RETRY_ATTEMPT: ${RETRY_ATTEMPT}`);

                    return setTimeout(
                        () => makeApiCall(apiUrl, callbackMethod),
                        RETRY_INTERVAL,
                    );
                }

                return callback(null, error);
            });

    makeApiCall(url, callback);
};

/**
 * @description Make external calls using the connect client
 * @param {string} url
 * @param {function} callback
 */
const externalGet = (url, callback) => {
    const MAX_RETRIES = config.retry.max_retries;
    const RETRY_INTERVAL = config.retry.timeout;
    let RETRY_ATTEMPT = 0;

    const makeExternalCall = (path, callBackFunction) =>
        acpGet(url, (statusCode, responseBody) => {
            // Additional, temporary log with url, statusCode and responseBody
            logger(logLevels.DEBUG, `[ACP GET Response] URL:${url}, STATUS_CODE: ${statusCode}, RESPONSE_BODY: ${responseBody}`);

            if ((statusCode >= 500 || statusCode === 404) && RETRY_ATTEMPT < MAX_RETRIES) {
                RETRY_ATTEMPT += 1;

                if (statusCode === 404) {
                    sessionStorage.setItem('SPOTLIGHT_RETRY_CALL', true);
                }

                logger(logLevels.DEBUG, `Retry call on enpoint:${url}, RETRY_ATTEMPT: ${RETRY_ATTEMPT}`);

                return setTimeout(
                    () => makeExternalCall(path, callBackFunction),
                    RETRY_INTERVAL,
                );
            } else if (statusCode === 200) {
                try {
                    const response = JSON.parse(responseBody);
                    return callback(response);
                } catch (err) {
                    return callback(null, err);
                }
            } else {
                return callback(null, {statusCode, responseBody});
            }
        });

    makeExternalCall(url, callback);
};

/**
 * @description Make axios post calls with retry
 * @param {string} url
 * @param {function} callback
 */
const apiPost = (url, payload, callback) => {
    const MAX_RETRIES = config.retry.max_retries;
    const RETRY_INTERVAL = config.retry.timeout;
    let RETRY_ATTEMPT = 0;

    let token = sessionStorage.getItem('SPOTLIGHT_TOKEN');
    // Get cwDashboard token from cookie if access_token param is missing
    if (!token) {
        token = getTokenFromCookies();
    }

    const options = {
        headers: {
            Authorization: `Bearer ${token}`,
        },
        timeout: config.timeouts.httpTimeout,
    };
    const endPointUrl = `${config.restApi.url}${url}`;
    const makeApiCall = (apiUrl, callbackMethod) =>
        axios.post(endPointUrl, payload, options)
            .then(response => callback(response.data))
            .catch(error => {
                // catch error only on the exchange token flow
                if (url === API.endpoint.oauth) {
                    // check if the error code is >5xx
                    const errorCode = String(error.response.status);
                    const isServerError =
                            errorCode.lastIndexOf('5', 0) === 0 && errorCode.length === 3;
                    const shouldRetryRequest =
                            isServerError || errorCode === '-1' || errorCode === '0';

                    if (shouldRetryRequest && RETRY_ATTEMPT < MAX_RETRIES) {
                        RETRY_ATTEMPT += 1;

                        logger(logLevels.DEBUG, `Retry call on enpoint:${url}, RETRY_ATTEMPT: ${RETRY_ATTEMPT}`);

                        return setTimeout(
                            () => makeApiCall(apiUrl, callbackMethod),
                            RETRY_INTERVAL,
                        );
                    }
                }

                return callback(null, error);
            });

    return makeApiCall(url, callback);
};

/**
 * @description Make external post using the connect client
 * @param {string} url
 * @param {json} payload
 * @param {function} callback
 */
const externalPost = (url, payload, callback) => {
    acpPost(url, payload, (statusCode, responseBody) => {
        logger(logLevels.DEBUG, `[ACP POST Response] URL:${url}, STATUS_CODE: ${statusCode}, RESPONSE_BODY: ${responseBody}`);

        if (statusCode === 200) {
            try {
                const response = JSON.parse(responseBody);
                return callback(response);
            } catch (err) {
                return callback(null, err);
            }
        } else {
            return callback(null, {statusCode, responseBody});
        }
    });
};

/**
 * @description Make a subscribe call using the connect client
 * @param {string} url
 * @param {function} callback
 */
const subscribe = (url, callback) => {
    acpSubscribe(url, (responseBody) => {
        // Log the url and the responseBody
        logger(logLevels.DEBUG, `[ACP Subscribe Response] URL:${url}, RESPONSE_BODY: ${responseBody}`);

        try {
            const response = JSON.parse(responseBody);
            return callback(response);
        } catch (err) {
            return callback(null, err);
        }
    });
};

/**
 * @description Make axios ping call
 * @param {string} url
 * @param {function} callback
 */
const ping = (url, callback) => {
    const MAX_RETRIES = config.retry.max_retries;
    const RETRY_INTERVAL = config.retry.timeout;
    let RETRY_ATTEMPT = 0;

    const endPointUrl = `${config.restApi.url}${url}`;
    const makeApiCall = (apiUrl, callbackMethod) =>
        axios.get(endPointUrl)
            .then(response => callback(response.headers))
            .catch(error => {
                // check if the error code is >5xx
                const errorCode = String(error.response.status);
                const isServerError =
                        errorCode.lastIndexOf('5', 0) === 0 && errorCode.length === 3;
                const shouldRetryRequest =
                        isServerError || errorCode === '-1' || errorCode === '0' || errorCode === '404';

                if (shouldRetryRequest && RETRY_ATTEMPT < MAX_RETRIES) {
                    RETRY_ATTEMPT += 1;

                    if (errorCode === 404) {
                        sessionStorage.setItem('SPOTLIGHT_RETRY_CALL', true);
                    }

                    logger(logLevels.DEBUG, `Retry call on enpoint:${url}, RETRY_ATTEMPT: ${RETRY_ATTEMPT}`);

                    return setTimeout(
                        () => makeApiCall(apiUrl, callbackMethod),
                        RETRY_INTERVAL,
                    );
                }

                return callback(null, error);
            });

    makeApiCall(url, callback);
};

/**
 * @description Make a fetch call for smart scan using the connect client
 * @param {function} callback
 */
const fetch = (callback) => {
    fetchSmartScanResults((responseBody) => {
        // Log the responseBody
        logger(logLevels.DEBUG, `[Fetch Smart Scan Results Response] RESPONSE_BODY: ${responseBody}`);

        try {
            const response = JSON.parse(responseBody);
            return callback(response);
        } catch (err) {
            return callback(null, err);
        }
    });
};

const API = {
    endpoint,
    get: (url, callback) =>
        isExternalMethodImplemented('acpGet') ? externalGet(url, callback) : apiGet(url, callback),
    post: (url, payload, callback) =>
        isExternalMethodImplemented('AcpBackendPost') ? externalPost(url, payload, callback) : apiPost(url, payload, callback),
    subscribe,
    ping,
    fetch,
};

export default API;
